import React, { useState, useEffect } from 'react';
import { stockAPI, propertyAPI, storeAPI, itemAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { DataTable } from '../components/DataTable';
import { History, TrendingUp, TrendingDown, Activity, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatNumber } from '../lib/utils';

export const StockAdjustmentsPage = () => {
  const [adjustments, setAdjustments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const adjustmentsRes = await stockAPI.getAdjustments({});
      setAdjustments(adjustmentsRes.data.data || []);
      setSummary(adjustmentsRes.data.summary || null);
    } catch (error) {
      toast.error('Failed to fetch adjustment history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    { 
      key: 'created_at', 
      label: 'Date & Time', 
      render: (val) => <span className="whitespace-nowrap">{formatDate(val)}</span>
    },
    { 
      key: 'item_name', 
      label: 'Item', 
      render: (val, row) => (
        <div>
          <span className="font-medium">{val}</span>
          {row.item_code && (
            <span className="ml-2 px-2 py-0.5 bg-muted rounded text-xs font-mono">{row.item_code}</span>
          )}
        </div>
      )
    },
    { 
      key: 'store_name', 
      label: 'Store', 
      render: (val, row) => (
        <div className="text-sm">
          <div>{val}</div>
          <div className="text-muted-foreground text-xs">{row.property_name}</div>
        </div>
      )
    },
    { 
      key: 'old_quantity', 
      label: 'Old Qty', 
      render: (val) => <span className="font-mono">{formatNumber(val)}</span>
    },
    { 
      key: 'new_quantity', 
      label: 'New Qty', 
      render: (val) => <span className="font-mono">{formatNumber(val)}</span>
    },
    { 
      key: 'adjustment', 
      label: 'Change', 
      render: (val) => {
        const isPositive = val > 0;
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
            isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {isPositive ? '+' : ''}{formatNumber(val)}
          </span>
        );
      }
    },
    { 
      key: 'reason', 
      label: 'Reason', 
      render: (val) => (
        <span className="text-sm truncate block max-w-[200px]" title={val}>{val || '-'}</span>
      )
    },
    { 
      key: 'adjusted_by_name', 
      label: 'Adjusted By', 
      render: (val) => <span className="text-sm text-muted-foreground">{val || '-'}</span>
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Stock Adjustment History</h1>
          <p className="text-muted-foreground mt-1">Track all stock quantity corrections and adjustments</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="rounded-full" data-testid="refresh-adjustments-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="summary-total-adjustments">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-blue-100">
                  <Activity className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Adjustments</p>
                  <p className="text-2xl font-bold">{formatNumber(summary.total_adjustments)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-positive-adjustments">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-100">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Increases</p>
                  <p className="text-2xl font-bold text-green-600">{formatNumber(summary.positive_adjustments)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-negative-adjustments">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-red-100">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Decreases</p>
                  <p className="text-2xl font-bold text-red-600">{formatNumber(summary.negative_adjustments)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="summary-net-adjustment">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${summary.net_adjustment >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <History className={`h-6 w-6 ${summary.net_adjustment >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Net Change</p>
                  <p className={`text-2xl font-bold ${summary.net_adjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.net_adjustment >= 0 ? '+' : ''}{formatNumber(summary.net_adjustment)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <DataTable
            module="stock_adjustments"
            data={adjustments}
            loading={loading}
            title="Stock Adjustment History"
            columns={columns}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default StockAdjustmentsPage;
